import { useState, useEffect, useCallback, useMemo } from "react";
import { RolePlay } from "../learning/roleplay";
import { AiChatProvider } from "../learning/AiChatContext";
import { apiFetch, getBackendUrl } from "../../../api";
import { trackEvent } from "../../../analytics/posthog";
import AiChatPopup from "../learning/components/AiChatPopup";
import WordReviewCard from "./WordReviewCard";
import s from "./ExtraPracticePanel.module.css";

const BACKEND = getBackendUrl();
const NODE_COLORS = ["gold", "amber", "emerald", "indigo", "rose", "purple"];

function getNodeColor(index) {
  return NODE_COLORS[index % NODE_COLORS.length];
}

function getStageName(stage) {
  const names = ["New", "Stage 1", "Stage 2", "Stage 3", "Stage 4", "Stage 5"];
  return names[stage] || `Stage ${stage}`;
}

function formatDueDate(dateStr) {
  if (!dateStr) return null;
  const due = new Date(dateStr);
  const now = new Date();
  const diffMs = now - due;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMs < 0) return "upcoming";
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "yesterday";
  return `${diffDays}d ago`;
}

export default function ExtraPracticePanel({ onPracticeComplete, onBackToStories }) {
  const [stories, setStories] = useState([]);
  const [flashcards, setFlashcards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeStoryId, setActiveStoryId] = useState(null);
  const [activeWordReview, setActiveWordReview] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [moduleContext, setModuleContext] = useState(null);

  const aiChatValue = useMemo(() => ({ setModuleContext }), []);

  const fetchDailyPractice = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/v1/daily-practice");
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || "Failed to fetch daily practice");
      }
      const data = await res.json();
      setStories(data.stories || []);
      setFlashcards(data.flashcards || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDailyPractice();
  }, [fetchDailyPractice]);

  const handlePickStory = useCallback((storyId) => {
    trackEvent("daily_practice_started", { type: "story", story_id: storyId });
    setActiveStoryId(storyId);
  }, []);

  const handleReviewComplete = useCallback(() => {
    trackEvent("daily_practice_completed", { type: "story", story_id: activeStoryId });
    setActiveStoryId(null);
    fetchDailyPractice();
    onPracticeComplete?.();
  }, [fetchDailyPractice, onPracticeComplete, activeStoryId]);

  const handleBack = useCallback(() => {
    setActiveStoryId(null);
  }, []);

  // Word review mode
  if (activeWordReview) {
    return (
      <AiChatProvider value={aiChatValue}>
        <div style={{ position: "relative", height: "100%" }}>
          <WordReviewCard
            flashcards={flashcards}
            onBack={() => setActiveWordReview(false)}
            onComplete={() => { trackEvent("daily_practice_completed", { type: "word_review" }); setActiveWordReview(false); fetchDailyPractice(); onPracticeComplete?.(); }}
            onAskAi={() => setShowAiChat(true)}
          />
          {showAiChat && (
            <AiChatPopup
              onClose={() => setShowAiChat(false)}
              context={moduleContext ?? { origin: "daily_practice" }}
            />
          )}
        </div>
      </AiChatProvider>
    );
  }

  // RolePlay in review mode
  if (activeStoryId) {
    return (
      <AiChatProvider value={aiChatValue}>
        <div style={{ position: "relative", height: "100%" }}>
          <RolePlay
            storyId={activeStoryId}
            reviewMode
            onBack={handleBack}
            onComplete={handleReviewComplete}
            onAskAi={() => setShowAiChat(true)}
          />


          {showAiChat && (
            <AiChatPopup
              onClose={() => setShowAiChat(false)}
              context={moduleContext ?? { origin: "daily_practice" }}
              storyId={activeStoryId}
            />
          )}
        </div>
      </AiChatProvider>
    );
  }

  const totalItems = stories.length + flashcards.length;
  const isEmpty = stories.length === 0 && flashcards.length === 0;

  /* ── Shared header ── */
  const Header = (
    <div className={s.header}>
      {onBackToStories && (
        <button className={s.backBtn} onClick={onBackToStories}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back to Stories
        </button>
      )}
      <div className={s.headerTitleGroup}>
        <div className={s.headerIcon}>
          <svg className={s.headerIconSvg} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
          </svg>
        </div>
        <h2 className={s.headerTitle}>Daily Practice</h2>
        <p className={s.headerSubtitle}>
          {loading ? "Loading..." : isEmpty ? "Spaced repetition for lasting results" : `${totalItems} review${totalItems !== 1 ? "s" : ""} due today`}
        </p>
      </div>

      {!isEmpty && !loading && (
        <>
          <div className={s.headerDivider}>
            <div className={s.headerDividerLine} />
            <div className={s.headerDividerDot} />
            <div className={s.headerDividerLine} />
          </div>
          <div className={s.headerStats}>
            {stories.length > 0 && (
              <div className={s.statPill}>
                <svg className={s.statIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15.182 15.182a4.5 4.5 0 0 1-6.364 0M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z" />
                </svg>
                <span className={s.statValue}>{stories.length}</span>
                <span className={s.statLabel}>{stories.length === 1 ? "story" : "stories"}</span>
              </div>
            )}
            {stories.length > 0 && flashcards.length > 0 && <div className={s.statDot} />}
            {flashcards.length > 0 && (
              <div className={s.statPill}>
                <svg className={s.statIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
                </svg>
                <span className={s.statValue}>{flashcards.length}</span>
                <span className={s.statLabel}>{flashcards.length === 1 ? "word" : "words"}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Loading
  if (loading) {
    return (
      <div className={s.panel}>
        {Header}
        <div className={s.content}>
          <div className={s.emptyState}>
            <div className={s.spinner} />
            <p className={s.emptyDesc}>Preparing your reviews</p>
          </div>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className={s.panel}>
        {Header}
        <div className={s.content}>
          <div className={s.emptyState}>
            <h3 className={s.emptyTitle}>Something went wrong</h3>
            <p className={s.emptyDesc}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={s.panel}>
      {Header}

      <div className={s.content}>
        {isEmpty ? (
          <div className={s.emptyState}>
            {onBackToStories ? (
              <>
                <div className={s.emptyIconMain} style={{ position: "relative", marginBottom: 24 }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                </div>
                <h3 className={s.emptyTitle}>All done!</h3>
                <p className={s.emptyDesc}>
                  Great work! You've completed all your reviews for now.
                </p>
                <button className={s.cardAction} onClick={onBackToStories} style={{ maxWidth: 260, marginTop: 20 }}>
                  <span className={s.cardActionText}>Back to Stories</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                  </svg>
                </button>
              </>
            ) : (
              <>
                <div className={s.emptyIllustration}>
                  <div className={s.emptyOrbit}>
                    <div className={`${s.emptyOrbitDot} ${s.emptyOrbitDot1}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <div className={`${s.emptyOrbitDot} ${s.emptyOrbitDot2}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19.114 5.636a9 9 0 0 1 0 12.728M16.463 8.288a5.25 5.25 0 0 1 0 7.424M6.75 8.25l4.72-4.72a.75.75 0 0 1 1.28.53v15.88a.75.75 0 0 1-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.009 9.009 0 0 1 2.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75Z" />
                      </svg>
                    </div>
                    <div className={`${s.emptyOrbitDot} ${s.emptyOrbitDot3}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                      </svg>
                    </div>
                  </div>
                  <div className={s.emptyIconMain}>
                    <img src="/workout.svg" alt="" width="28" height="28" />
                  </div>
                </div>

                <h3 className={s.emptyTitle}>Come back later</h3>
                <p className={s.emptyDesc}>
                  Finished lessons come back here for review so you don't forget them.
                </p>

                <div className={s.emptySteps}>
                  <div className={s.emptyStep}>
                    <div className={s.emptyStepNum}>1</div>
                    <span className={s.emptyStepText}>Complete a lesson</span>
                  </div>
                  <div className={s.emptyStepArrow}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </div>
                  <div className={s.emptyStep}>
                    <div className={s.emptyStepNum}>2</div>
                    <span className={s.emptyStepText}>Reviews appear here</span>
                  </div>
                  <div className={s.emptyStepArrow}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </div>
                  <div className={s.emptyStep}>
                    <div className={s.emptyStepNum}>3</div>
                    <span className={s.emptyStepText}>Knowledge sticks</span>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className={s.questPath}>
            {/* Word Flashcards */}
            {flashcards.length > 0 && (
              <div className={s.questStepFull} style={{ animationDelay: "0.05s" }}>
                <button className={s.questInfo} onClick={() => setActiveWordReview(true)}>
                  <div className={s.questInfoTop}>
                    <span className={s.typeBadge}>Word Review</span>
                  </div>
                  <h3 className={s.questTitle}>Word Flashcards</h3>
                  <p className={s.questDesc}>{flashcards.length} word{flashcards.length !== 1 ? "s" : ""} to review</p>
                  <div className={s.cardAction}>
                    <span className={s.cardActionText}>Start Review</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </div>
                </button>
              </div>
            )}

            {/* Story Reviews */}
            {stories.map((story, i) => (
              <div
                key={story.story_id}
                className={s.questStepFull}
                style={{ animationDelay: `${0.1 + (i + (flashcards.length > 0 ? 1 : 0)) * 0.06}s` }}
              >
                <button className={s.questInfo} onClick={() => handlePickStory(story.story_id)}>
                  <div className={s.questCardRow}>
                    <div className={`${s.cardAvatar} ${s[`avatar_${getNodeColor(i)}`]}`}>
                      <img
                        className={s.cardAvatarImg}
                        src={`${BACKEND}/api/v1/admin/story-avatar/${story.story_id}`}
                        alt=""
                      />
                    </div>
                    <div className={s.cardTextBlock}>
                      <div className={s.questInfoTop}>
                        <span className={s.typeBadge}>Story Review</span>
                      </div>
                      <h3 className={s.questTitle}>{story.topic_in_swedish}</h3>
                      <p className={s.questDesc}>{story.topic_in_english}</p>
                      <div className={s.questMeta}>
                        <span className={`${s.modeBadge} ${s.modeBadge_gold}`}>
                          {getStageName(story.review_stage)}
                        </span>
                        {story.next_review_at && (
                          <span className={s.dueBadge}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M12 6v6l4 2" />
                            </svg>
                            Due {formatDueDate(story.next_review_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={s.cardAction}>
                    <span className={s.cardActionText}>Start Review</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </div>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAiChat && (
        <AiChatPopup
          onClose={() => setShowAiChat(false)}
          context={{ origin: "daily_practice" }}
        />
      )}
    </div>
  );
}
